import { useEffect, useMemo, useState } from "react";
import Editor, { useMonaco } from "@monaco-editor/react";
import type * as Monaco from "monaco-editor";
import { Maximize2, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { inferFormulaWarnings, parseAndResolve } from "@brewbase/lib";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LANGUAGE_ID, THEME_ID, registerFormulaLanguage } from "./language";
import { useFormulaContext } from "./hooks/useFormulaContext";
import { FullscreenFormula } from "./FullscreenFormula";
import type { FormulaContextInput } from "./context";

interface FormulaFieldProps {
  value: string;
  /** Persisted only when the user explicitly saves in the dialog. */
  onSave: (value: string) => void | Promise<void>;
  /** Context descriptor for the site being edited. */
  context: FormulaContextInput;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  /** Dialog title. */
  title?: string;
}

const LINE_HEIGHT = 20;
const PADDING_Y = 4;
const TRIGGER_HEIGHT = LINE_HEIGHT + PADDING_Y * 2;

const READ_ONLY_OPTIONS: Monaco.editor.IStandaloneEditorConstructionOptions = {
  readOnly: true,
  domReadOnly: true,
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 12,
  lineHeight: LINE_HEIGHT,
  minimap: { enabled: false },
  lineNumbers: "off",
  lineDecorationsWidth: 0,
  lineNumbersMinChars: 0,
  glyphMargin: false,
  folding: false,
  wordWrap: "off",
  renderLineHighlight: "none",
  scrollbar: {
    vertical: "hidden",
    horizontal: "hidden",
    handleMouseWheel: false,
  },
  overviewRulerLanes: 0,
  overviewRulerBorder: false,
  hideCursorInOverviewRuler: true,
  scrollBeyondLastLine: false,
  scrollBeyondLastColumn: 0,
  padding: { top: PADDING_Y, bottom: PADDING_Y },
  contextmenu: false,
  cursorStyle: "line-thin",
  renderValidationDecorations: "off",
  guides: { indentation: false },
};

/**
 * Read-only formula display that opens the fullscreen editor on click. All
 * editing goes through the dialog's explicit Save — there is no inline editing.
 */
export function FormulaField({
  value,
  onSave,
  context,
  disabled,
  className,
  placeholder,
  title,
}: FormulaFieldProps) {
  const monaco = useMonaco();
  const [fullscreen, setFullscreen] = useState(false);
  const formulaContext = useFormulaContext(context);

  useEffect(() => {
    if (!monaco) return;
    registerFormulaLanguage(monaco);
  }, [monaco]);

  const unitWarnings = useMemo(
    () =>
      formulaContext && value.trim()
        ? inferFormulaWarnings(value, formulaContext)
        : [],
    [value, formulaContext],
  );

  const diagnosticSummary = useMemo(() => {
    if (!formulaContext || !value.trim()) return null;
    const result = parseAndResolve(value, formulaContext);
    if (result.ok) return null;
    if (result.phase === "parse") return result.message;
    const first = result.errors[0];
    const more = result.errors.length - 1;
    return more > 0 ? `${first.message} (+${more} more)` : first.message;
  }, [value, formulaContext]);

  const open = () => {
    if (!disabled) setFullscreen(true);
  };

  const trigger = (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Edit formula"
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        }}
        className={cn(
          "relative overflow-hidden border border-olive-7 bg-white",
          disabled
            ? "opacity-60"
            : "cursor-pointer hover:border-olive-9 focus:border-olive-9 focus:outline-none",
          // A formula that computes but needs checking: orange, message on hover.
          unitWarnings.length > 0 &&
            "border-amber-600 hover:border-amber-700 focus:border-amber-700",
        )}
      >
        {value ? (
          <Editor
            value={value}
            language={LANGUAGE_ID}
            onMount={(_editor, m) => m.editor.setTheme(THEME_ID)}
            height={TRIGGER_HEIGHT}
            options={READ_ONLY_OPTIONS}
          />
        ) : (
          <div
            className="px-2 text-xs italic text-olive-9 font-mono"
            style={{ height: TRIGGER_HEIGHT, lineHeight: `${TRIGGER_HEIGHT}px` }}
          >
            {placeholder ?? "No formula defined"}
          </div>
        )}
        {/* Shield: keep the read-only editor from capturing clicks so the whole
            field acts as a single trigger. */}
        <div className="absolute inset-0" aria-hidden />
        {(!disabled || unitWarnings.length > 0) && (
          <span className="absolute top-0 right-0 flex h-full items-center gap-1 px-1.5 text-olive-10 pointer-events-none">
            {unitWarnings.length > 0 && (
              <TriangleAlert size={14} className="text-amber-600" />
            )}
            {!disabled && <Maximize2 size={12} />}
          </span>
        )}
      </div>
  );

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {unitWarnings.length > 0 ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="top">
            <p className="max-w-xs text-xs">{unitWarnings.join(" ")}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      {unitWarnings.length > 0 && (
        <div className="flex items-start gap-1.5 bg-warning/20 px-2 py-1.5">
          <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warning-foreground" />
          <p className="text-xs text-warning-foreground">
            {unitWarnings.join(" ")}
          </p>
        </div>
      )}
      {diagnosticSummary && (
        <div
          className="truncate text-xs text-tomato-11 font-mono"
          title={diagnosticSummary}
        >
          {diagnosticSummary}
        </div>
      )}
      <FullscreenFormula
        open={fullscreen}
        onOpenChange={setFullscreen}
        value={value}
        onSave={onSave}
        context={context}
        title={title}
      />
    </div>
  );
}
